import { getOrDecodeAudio } from '../deps/composition-runtime'
import { resolveMediaUrl } from '../deps/media-library-resolver'
import { useTimelineStore } from '../stores/timeline-store'
import { createLogger } from '@/shared/logging/logger'
import type { FillerAudioConfidence, FillerRange, FillerRangesByItemId } from './filler-word-removal-preview'

const logger = createLogger('FillerAudioConfidence')

const CLAP_MODEL_ID = 'Xenova/clap-htsat-unfused'
const CLAP_SAMPLE_RATE = 48_000
const MIN_AUDIO_WINDOW_SEC = 0.35
const WINDOW_CONTEXT_SEC = 0.08
const MAX_CONCURRENT_SCORES = 2
const SCORE_CACHE_MAX_ENTRIES = 500

const FILLER_LABELS = [
  'filler word',
  'hesitation sound',
  'person saying um',
  'person saying uh',
  'person hesitating while speaking',
] as const

const NON_FILLER_LABELS = ['normal speech', 'silence', 'music', 'background noise'] as const
const CANDIDATE_LABELS = [...FILLER_LABELS, ...NON_FILLER_LABELS]

type ZeroShotAudioClassifier = (
  audio: Float32Array,
  labels: readonly string[],
  options?: { hypothesis_template?: string },
) => Promise<Array<{ label: string; score: number }>>

type TransformersModule = {
  env: {
    useBrowserCache: boolean
    allowLocalModels: boolean
  }
  pipeline: (
    task: 'zero-shot-audio-classification',
    model: string,
    options?: { device?: 'webgpu' | 'wasm'; dtype?: string },
  ) => Promise<ZeroShotAudioClassifier>
}

let classifierPromise: Promise<ZeroShotAudioClassifier> | null = null
const scoreCache = new Map<string, FillerAudioConfidence>()

function getRangeCacheKey(itemId: string, range: FillerRange): string {
  return `${itemId}:${range.sourceStartSec?.toFixed(3) ?? 'na'}:${range.sourceEndSec?.toFixed(3) ?? 'na'}:${range.text}`
}

function getCachedScore(cacheKey: string): FillerAudioConfidence | undefined {
  const cached = scoreCache.get(cacheKey)
  if (!cached) return undefined
  scoreCache.delete(cacheKey)
  scoreCache.set(cacheKey, cached)
  return cached
}

function setCachedScore(cacheKey: string, confidence: FillerAudioConfidence): void {
  if (scoreCache.size >= SCORE_CACHE_MAX_ENTRIES) {
    const oldestKey = scoreCache.keys().next().value
    if (oldestKey !== undefined) {
      scoreCache.delete(oldestKey)
    }
  }
  scoreCache.set(cacheKey, confidence)
}

async function getClassifier(): Promise<ZeroShotAudioClassifier> {
  if (classifierPromise) return classifierPromise

  classifierPromise = (async () => {
    const { env, pipeline } = (await import('@huggingface/transformers')) as TransformersModule
    env.useBrowserCache = true
    env.allowLocalModels = false

    try {
      return await pipeline('zero-shot-audio-classification', CLAP_MODEL_ID, {
        device: 'webgpu',
        dtype: 'q8',
      })
    } catch (error) {
      logger.warn('CLAP WebGPU initialization failed, falling back to WASM', { error })
      return pipeline('zero-shot-audio-classification', CLAP_MODEL_ID, {
        device: 'wasm',
        dtype: 'q8',
      })
    }
  })()

  try {
    return await classifierPromise
  } catch (error) {
    classifierPromise = null
    throw error
  }
}

function mixDownToMono(buffer: AudioBuffer, startSec: number, endSec: number): Float32Array {
  const sampleRate = buffer.sampleRate
  const startSample = Math.max(0, Math.floor(startSec * sampleRate))
  const endSample = Math.min(buffer.length, Math.ceil(endSec * sampleRate))
  const length = Math.max(1, endSample - startSample)
  const output = new Float32Array(length)

  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel)
    for (let index = 0; index < length; index += 1) {
      output[index] = (output[index] ?? 0) + (data[startSample + index] ?? 0)
    }
  }

  const gain = 1 / Math.max(1, buffer.numberOfChannels)
  for (let index = 0; index < output.length; index += 1) {
    output[index] = (output[index] ?? 0) * gain
  }

  return output
}

function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input

  const outputLength = Math.max(1, Math.round((input.length * toRate) / fromRate))
  const output = new Float32Array(outputLength)
  const ratio = fromRate / toRate

  for (let index = 0; index < outputLength; index += 1) {
    const sourceIndex = index * ratio
    const leftIndex = Math.floor(sourceIndex)
    const rightIndex = Math.min(input.length - 1, leftIndex + 1)
    const mix = sourceIndex - leftIndex
    const left = input[leftIndex] ?? 0
    const right = input[rightIndex] ?? left
    output[index] = left + (right - left) * mix
  }

  return output
}

function getRangeAudio(buffer: AudioBuffer, range: FillerRange): Float32Array {
  const start = range.sourceStartSec ?? 0
  const end = Math.max(start, range.sourceEndSec ?? start)
  const midpoint = (start + end) / 2
  const halfWindow = Math.max(MIN_AUDIO_WINDOW_SEC, end - start) / 2
  const windowStart = Math.max(0, midpoint - halfWindow - WINDOW_CONTEXT_SEC)
  const windowEnd = Math.min(buffer.duration, midpoint + halfWindow + WINDOW_CONTEXT_SEC)
  const mono = mixDownToMono(buffer, windowStart, windowEnd)
  return resampleLinear(mono, buffer.sampleRate, CLAP_SAMPLE_RATE)
}

function classifyConfidence(scores: Array<{ label: string; score: number }>): FillerAudioConfidence {
  const fillerScores = scores.filter((entry) => FILLER_LABELS.includes(entry.label as never))
  const nonFillerScores = scores.filter((entry) => NON_FILLER_LABELS.includes(entry.label as never))
  const bestFiller = fillerScores.toSorted((left, right) => right.score - left.score)[0]
  const bestNonFiller = nonFillerScores.toSorted((left, right) => right.score - left.score)[0]
  const fillerScore = bestFiller?.score ?? 0
  const nonFillerScore = bestNonFiller?.score ?? 0
  const margin = fillerScore - nonFillerScore

  const level =
    fillerScore >= 0.42 && margin >= 0.12
      ? 'high'
      : fillerScore >= 0.28 && margin >= 0.02
        ? 'medium'
        : 'low'

  return {
    level,
    score: fillerScore,
    fillerScore,
    nonFillerScore,
    label: bestFiller?.label ?? 'filler word',
  }
}

function unknownConfidence(label: string): FillerAudioConfidence {
  return {
    level: 'unknown',
    score: 0,
    fillerScore: 0,
    nonFillerScore: 0,
    label,
  }
}

async function scoreOneRange(
  classifier: ZeroShotAudioClassifier,
  audioBuffer: AudioBuffer,
  itemId: string,
  range: FillerRange,
): Promise<FillerRange> {
  if (range.sourceStartSec === undefined || range.sourceEndSec === undefined) {
    return { ...range, audioConfidence: unknownConfidence('missing source timing') }
  }

  const cacheKey = getRangeCacheKey(itemId, range)
  const cached = getCachedScore(cacheKey)
  if (cached) {
    return { ...range, audioConfidence: cached }
  }

  const audio = getRangeAudio(audioBuffer, range)
  const scores = await classifier(audio, CANDIDATE_LABELS, {
    hypothesis_template: 'This audio contains {}.',
  })
  const confidence = classifyConfidence(scores)
  setCachedScore(cacheKey, confidence)
  return { ...range, audioConfidence: confidence }
}

async function runLimited<T, R>(
  inputs: readonly T[],
  worker: (input: T) => Promise<R>,
  concurrency: number,
): Promise<R[]> {
  const results: R[] = []
  let nextIndex = 0

  async function runNext(): Promise<void> {
    const index = nextIndex
    nextIndex += 1
    if (index >= inputs.length) return
    results[index] = await worker(inputs[index]!)
    await runNext()
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, inputs.length) }, runNext))
  return results
}

export async function scoreFillerRangesWithClap(
  rangesByItemId: FillerRangesByItemId,
): Promise<FillerRangesByItemId> {
  const classifier = await getClassifier()
  const itemsById = new Map(useTimelineStore.getState().items.map((item) => [item.id, item]))
  const scored: FillerRangesByItemId = {}

  for (const [itemId, ranges] of Object.entries(rangesByItemId)) {
    const item = itemsById.get(itemId)
    if (!item || (item.type !== 'audio' && item.type !== 'video') || !item.mediaId) {
      scored[itemId] = ranges.map((range) => ({
        ...range,
        audioConfidence: unknownConfidence('unavailable media'),
      }))
      continue
    }

    try {
      const url = await resolveMediaUrl(item.mediaId)
      const audioBuffer = await getOrDecodeAudio(item.mediaId, url)
      scored[itemId] = await runLimited(
        ranges,
        (range) => scoreOneRange(classifier, audioBuffer, itemId, range),
        MAX_CONCURRENT_SCORES,
      )
    } catch (error) {
      logger.warn('Audio confidence scoring failed for item', { itemId, mediaId: item.mediaId, error })
      scored[itemId] = ranges.map((range) => ({
        ...range,
        audioConfidence: unknownConfidence('audio review failed'),
      }))
    }
  }

  return scored
}